
import React, {
  useEffect,
  useState,
} from "react";

import {
  getRoomAvailability,
} from "../../api/bookingEngine";

import BookingNavbarHero from "./Components/BookingNavbarHero";
import BookingSearch from "./Components/BookingSearch";
import BookingFilters from "./Components/BookingFilters";
import BookingRoomList from "./Components/BookingRoomList";
import BookingModal from "./Components/BookingModal";
import BookingFooter from "./Components/BookingFooter";

import useBookingProperty from "./hooks/useBookingProperty";
import useBookingRooms from "./hooks/useBookingRooms";
import useBookingRates from "./hooks/useBookingRates";
import useBookingFilters from "./hooks/useBookingFilters";
import useBookingModal from "./hooks/useBookingModal";

import "./bookingEngine.css";


/* =========================================================
   BOOKING ENGINE
========================================================= */

const BookingEngine = () => {

  /* =========================================================
     SEARCH
  ========================================================= */

  const [
    searchDetails,
    setSearchDetails,
  ] = useState({

    checkIn: "",

    checkOut: "",

    adults: 1,

    children: 0,

  });


  /* =========================================================
     DEBUG SEARCH
  ========================================================= */

  useEffect(() => {

    console.log(
      "🔄 SEARCH DETAILS STATE CHANGED:",
      searchDetails
    );

  }, [searchDetails]);


  /* =========================================================
     BOOKING ENGINE MOUNT
  ========================================================= */

  useEffect(() => {

    console.log(
      "🟢 BOOKING ENGINE MOUNTED"
    );


    return () => {

      console.log(
        "🔴 BOOKING ENGINE UNMOUNTED"
      );

    };

  }, []);


  /* =========================================================
     PROPERTY
  ========================================================= */

  const propertyState =
    useBookingProperty();


  const {
    property,
    propertyLoading,
    propertyError,
    subdomain,
  } = propertyState;


  /* =========================================================
     ROOMS
  ========================================================= */

  const roomState =
    useBookingRooms({

      subdomain,

    });


  const {
    allRooms,
    roomsData,
    setRooms,
    roomCategories,
    setRoomCategories,
    loadingRooms,
    searchingAvailability,
    setSearchingAvailability,
  } = roomState;


  /* =========================================================
     PUBLIC BOOKING ENGINE RATES
     
     IMPORTANT:
     Rates are NOT taken from rateLogs.

     useBookingRates is responsible for loading rates
     from the public booking-engine rate API using:

       /api/booking-engine/:subdomain/rates
  ========================================================= */

  const rateCalculation =
    useBookingRates({

      roomsData,

      property,

      subdomain,

      searchDetails,

    });


  const {
    rooms:
      roomsWithChannelManagerRates,

    ratesLoading,

  } = rateCalculation;


  /* =========================================================
     FILTERS
  ========================================================= */

  const filterState =
    useBookingFilters({

      rooms:
        roomsWithChannelManagerRates,

    });


  const {
    filters,
    setFilters,
    filteredRooms,
    toggleRoomType,
    resetFilters,
  } = filterState;


  /* =========================================================
     BOOKING
  ========================================================= */

  const bookingState =
    useBookingModal({

      searchDetails,

      subdomain,

    });


  const {
    offers,

    selectedRoom,

    bookingSearchDetails,

    isBookingModalOpen,

    bookingLoading,

    guestForm,

    handleRoomSelect,

    handleGuestChange,

    handleBookingSubmit,

    closeBookingModal,

  } = bookingState;


  /* =========================================================
     MOBILE FILTER
  ========================================================= */

  const [
    mobileFilterOpen,
    setMobileFilterOpen,
  ] = useState(false);


  /* =========================================================
     SEARCH / AVAILABILITY
  ========================================================= */

  const handleSearch =
    async () => {

      /* =====================================================
         NO DATES
      ===================================================== */

      if (
        !searchDetails.checkIn &&
        !searchDetails.checkOut
      ) {

        setRooms(
          allRooms
        );

        return;

      }


      /* =====================================================
         ONLY ONE DATE
      ===================================================== */

      if (
        !searchDetails.checkIn ||
        !searchDetails.checkOut
      ) {

        setRooms(
          allRooms
        );

        return;

      }


      /* =====================================================
         VALIDATE DATES
      ===================================================== */

      if (
        searchDetails.checkIn >=
        searchDetails.checkOut
      ) {

        alert(
          "Check-out date must be after check-in date."
        );

        return;

      }


      try {

        setSearchingAvailability(
          true
        );


        console.log(
          "🔎 SEARCHING AVAILABILITY:",
          {
            subdomain,

            checkIn:
              searchDetails.checkIn,

            checkOut:
              searchDetails.checkOut,

            adults:
              searchDetails.adults,

            children:
              searchDetails.children,

          }
        );


        /* ===================================================
           CHECK ALL ROOM TYPES
        =================================================== */

        const results =
          await Promise.all(

            allRooms.map(
              async (room) => {

                try {

                  const roomTypeId =
                    room?.roomTypeId ||
                    room?.id ||
                    room?._id;


                  if (!roomTypeId) {

                    console.warn(
                      "⚠️ ROOM TYPE ID MISSING:",
                      room
                    );

                    return {

                      ...room,

                      availableRooms:
                        0,

                      availability:
                        [],

                    };

                  }


                  console.log(
                    "🔎 CHECKING AVAILABILITY:",
                    {

                      room:
                        room?.name,

                      roomTypeId,

                      subdomain,

                      checkIn:
                        searchDetails.checkIn,

                      checkOut:
                        searchDetails.checkOut,

                    }
                  );


                  /* ==========================================
                     AVAILABILITY API
                     
                     IMPORTANT:
                     This API is ONLY responsible for
                     availability.

                     Rates are handled separately by
                     useBookingRates.
                  ========================================== */

                  const response =
                    await getRoomAvailability({

                      subdomain,

                      roomTypeId,

                      checkIn:
                        searchDetails.checkIn,

                      checkOut:
                        searchDetails.checkOut,

                    });


                  console.log(
                    "📅 AVAILABILITY RESPONSE:",
                    room?.name,
                    response
                  );


                  const dates =
                    Array.isArray(
                      response?.dates
                    )
                      ? response.dates
                      : [];


                  /* ==========================================
                     AVAILABLE ROOM COUNT

                     For a multi-night stay, the number of
                     rooms available is the minimum available
                     count across all nights.
                  ========================================== */

                  const availableRooms =
                    dates.length > 0

                      ? Math.min(
                          ...dates.map(
                            (item) =>
                              Number(
                                item?.available
                              ) || 0
                          )
                        )

                      : 0;


                  return {

                    ...room,

                    availability:
                      dates,

                    availableRooms,

                  };


                } catch (error) {

                  console.error(
                    "❌ AVAILABILITY ERROR:",
                    room?.name,
                    error
                  );


                  return {

                    ...room,

                    availableRooms:
                      0,

                    availability:
                      [],

                  };

                }

              }
            )

          );


        /* ===================================================
           AVAILABLE ROOMS ONLY
        =================================================== */

        const availableResults =
          results.filter(
            (room) =>
              Number(
                room?.availableRooms
              ) > 0
          );


        /* ===================================================
           UPDATE ROOMS
        =================================================== */

        setRooms(
          availableResults
        );


        /* ===================================================
           CATEGORY COUNTS
        =================================================== */

        const categoryMap =
          {};


        availableResults.forEach(
          (room) => {

            const category =
              room?.category ||
              room?.name ||
              "Other";


            if (
              !categoryMap[
                category
              ]
            ) {

              categoryMap[
                category
              ] = {

                id:
                  category,

                name:
                  category,

                totalRooms:
                  0,

                availableRooms:
                  0,

              };

            }


            categoryMap[
              category
            ].totalRooms += 1;


            categoryMap[
              category
            ].availableRooms +=
              Number(
                room?.availableRooms ||
                0
              );

          }
        );


        setRoomCategories(
          Object.values(
            categoryMap
          )
        );


        console.log(
          "✅ SEARCH AVAILABILITY RESULTS:",
          availableResults
        );


      } catch (error) {

        console.error(
          "❌ ROOM AVAILABILITY SEARCH FAILED:",
          error
        );

      } finally {

        setSearchingAvailability(
          false
        );

      }

    };


  /* =========================================================
     PROPERTY LOADING
  ========================================================= */

  if (
    propertyLoading
  ) {

    return (

      <div className="booking-engine-loading">

        Loading property...

      </div>

    );

  }


  /* =========================================================
     PROPERTY ERROR
  ========================================================= */

  if (
    propertyError
  ) {

    return (

      <div className="booking-engine-error">

        <h2>
          Unable to load property
        </h2>

        <p>
          {propertyError}
        </p>

      </div>

    );

  }


  /* =========================================================
     RENDER
  ========================================================= */

  return (

    <div className="booking-engine">


      {/* =====================================================
          NAVBAR + HERO
      ===================================================== */}

      <BookingNavbarHero
        property={
          property
        }
      />


      {/* =====================================================
          SEARCH
      ===================================================== */}

      <BookingSearch

        searchDetails={
          searchDetails
        }

        setSearchDetails={
          setSearchDetails
        }

        handleSearch={
          handleSearch
        }

        loadingRooms={
          loadingRooms
        }

        searchingAvailability={
          searchingAvailability ||
          ratesLoading
        }

        property={
          property
        }

      />


      {/* =====================================================
          ROOMS
      ===================================================== */}

      <main
        className="be-main"
        id="rooms"
      >


        {/* =================================================
            MOBILE FILTER
        ================================================= */}

        <button
          type="button"
          className="be-mobile-filter-button"
          onClick={() =>
            setMobileFilterOpen(
              true
            )
          }
        >

          ☰ Filters & Sort

        </button>


        {/* =================================================
            CONTENT
        ================================================= */}

        <div className="be-content-layout">


          {/* =================================================
              FILTER
          ================================================= */}

          <BookingFilters

            filters={
              filters
            }

            setFilters={
              setFilters
            }

            toggleRoomType={
              toggleRoomType
            }

            resetFilters={
              resetFilters
            }

            mobileFilterOpen={
              mobileFilterOpen
            }

            setMobileFilterOpen={
              setMobileFilterOpen
            }

            roomCategories={
              roomCategories
            }

          />


          {/* =================================================
              ROOM LIST
          ================================================= */}

          <BookingRoomList

            rooms={
              filteredRooms
            }

            offers={
              offers
            }

            loading={
              loadingRooms
            }

            searching={
              searchingAvailability ||
              ratesLoading
            }

            onBook={
              handleRoomSelect
            }

            onResetFilters={
              resetFilters
            }

          />

        </div>

      </main>


      {/* =====================================================
          FOOTER
      ===================================================== */}

      <BookingFooter
        property={
          property
        }
      />

<BookingModal

  isOpen={
    isBookingModalOpen
  }

  selectedRoom={
    selectedRoom
  }

  searchDetails={
    bookingSearchDetails
  }

  guestForm={
    guestForm
  }

  bookingLoading={
    bookingLoading
  }

  offers={
    offers
  }

  onClose={
    closeBookingModal
  }

  onGuestChange={
    handleGuestChange
  }

  onSubmit={
    handleBookingSubmit
  }

/>

    </div>

  );

};


export default BookingEngine;